'use client';

import { TrendingUp, Brain, Network, Leaf, Cloud } from 'lucide-react';
import { useState } from 'react';

interface OfferingsSectionProps {
  selectedOffering: string | null;
  onOfferingSelect: (id: string) => void;
}

const OfferingsSection = ({ selectedOffering, onOfferingSelect }: OfferingsSectionProps) => {
  const offerings = [
    {
      id: 'carbon-marketplace',
      icon: TrendingUp,
      title: 'Carbon Credit Marketplace Platform',
      subtitle: 'Blockchain-Verified Trading Ecosystem',
      description: 'Transform from service provider to marketplace orchestrator with our blockchain-verified carbon credit exchange platform.',
      features: [
        'Direct P2P trading between credit generators and buyers',
        'Blockchain verification and smart contract automation',
        '3-5% transaction fees on every trade',
        'Real-time market pricing and trend analysis',
        'Automated compliance and regulatory reporting'
      ],
      technologies: ['Blockchain', 'Smart Contracts', 'AI Price Discovery', 'MRV Integration'],
      impactMetrics: {
        revenue: '₹500Cr+ Annual Transaction Volume',
        efficiency: '80% Reduction in Trading Time',
        reach: 'Global Marketplace Access'
      },
      color: 'from-emerald-500 to-teal-600',
      bgColor: 'from-emerald-50 to-teal-50'
    },
    {
      id: 'predictive-analytics',
      icon: Brain,
      title: 'AI-Powered Predictive ESG Analytics',
      subtitle: 'Future-Ready Intelligence Platform',
      description: 'Advanced AI algorithms that forecast ESG risks 2-3 years ahead using satellite data, IoT sensors, and machine learning models.',
      features: [
        'Risk prediction engine with 95% accuracy',
        'Regulatory change alerts and compliance forecasting',
        'Investment impact scoring for portfolio optimization',
        'Satellite data integration for environmental monitoring',
        'Real-time anomaly detection and alerting'
      ],
      technologies: ['Machine Learning', 'Satellite AI', 'Predictive Modeling', 'IoT Analytics'],
      impactMetrics: {
        revenue: '₹10L-50L Per Client Annually',
        efficiency: '70% Faster Risk Detection',
        reach: '2-3 Year Forecast Horizon'
      },
      color: 'from-blue-500 to-cyan-600',
      bgColor: 'from-blue-50 to-cyan-50'
    },
    {
      id: 'value-chain-network',
      icon: Network,
      title: 'Value Chain ESG Network Effect',
      subtitle: 'Supply Chain ESG Passport System',
      description: 'Create exponential value through network effects — every supplier gets ESG scoring and certification, cascading compliance throughout the ecosystem.',
      features: [
        'Supply Chain ESG Passport for every vendor',
        'Automated ESG scoring and certification',
        'Cascading compliance from Top 500 to entire supply chain',
        'Network revenue scaling (LinkedIn model for ESG)',
        'Real-time supply chain risk monitoring'
      ],
      technologies: ['Network Analytics', 'Blockchain Credentials', 'API Integration', 'Compliance Automation'],
      impactMetrics: {
        revenue: '₹1-5L Per Supplier Node',
        efficiency: 'Exponential Network Growth',
        reach: '10,000+ Supply Chain Partners'
      },
      color: 'from-purple-500 to-pink-600',
      bgColor: 'from-purple-50 to-pink-50'
    },
    {
      id: 'restoration-service',
      icon: Leaf,
      title: 'Global Restoration-as-a-Service',
      subtitle: 'Ecological Recovery Platform',
      description: 'Scale restoration projects globally into a profitable service model with carbon credit farming and restoration bonds.',
      features: [
        'Carbon Credit Farming on degraded lands',
        'Restoration Bonds backed by ecological recovery',
        'Climate insurance for restoration projects',
        'Global land acquisition and leasing network',
        'Satellite-verified restoration monitoring'
      ],
      technologies: ['Remote Sensing', 'Carbon Modeling', 'Blockchain Credits', 'Climate Analytics'],
      impactMetrics: {
        revenue: '₹100Cr+ Land Portfolio Value',
        efficiency: '5-10 Year ROI Horizon',
        reach: 'Multi-Country Operations'
      },
      color: 'from-green-500 to-emerald-600',
      bgColor: 'from-green-50 to-emerald-50'
    },
    {
      id: 'esg-platform',
      icon: Cloud,
      title: 'ESG-as-a-Platform SaaS Model',
      subtitle: 'The ESG Operating System',
      description: 'Position GenCarbon as the "ESG Operating System" that every company needs — white-label solutions, API monetization, and subscription tiers from SME to enterprise.',
      features: [
        'White-label solutions for global consultancies',
        'API monetization for ESG data integration',
        'Subscription tiers: ₹1L-10L recurring per client',
        'Data licensing to financial institutions',
        'Certification authority for global ESG standards'
      ],
      technologies: ['Cloud SaaS', 'API Platform', 'Data Analytics', 'Certification Systems'],
      impactMetrics: {
        revenue: '₹1000Cr+ Platform Revenue Potential',
        efficiency: '90% Automation Rate',
        reach: 'Global Market Penetration'
      },
      color: 'from-orange-500 to-red-600',
      bgColor: 'from-orange-50 to-red-50'
    }
  ];

  const [expandedOffering, setExpandedOffering] = useState<string | null>(null);

  const handleOfferingClick = (id: string) => {
    setExpandedOffering(expandedOffering === id ? null : id);
    onOfferingSelect(id);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Five Core Technological Offerings
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Proprietary methodologies transforming sustainability challenges into billion-dollar opportunities
          </p>
        </div>

        {/* Offerings Grid */}
        <div className="space-y-6">
          {offerings.map((offering, index) => {
            const IconComponent = offering.icon;
            const isExpanded = expandedOffering === offering.id;

            return (
              <div
                key={offering.id}
                className={`relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden cursor-pointer ${
                  isExpanded ? 'ring-4 ring-emerald-500' : ''
                }`}
                onClick={() => handleOfferingClick(offering.id)}
              >
                {/* Gradient Border Effect */}
                <div className={`absolute inset-0 bg-gradient-to-r ${offering.color} opacity-0 hover:opacity-10 transition-opacity duration-300`} />

                {/* Main Content */}
                <div className="relative p-8">
                  {/* Header */}
                  <div className="flex items-start gap-6 mb-6">
                    <div className={`flex-shrink-0 p-4 rounded-xl bg-gradient-to-br ${offering.color}`}>
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-2xl font-bold text-slate-900">{offering.title}</h3>
                        <svg
                          className={`w-6 h-6 text-slate-400 transition-transform duration-300 ${
                            isExpanded ? 'rotate-180' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                      <p className="text-lg font-semibold text-emerald-600 mb-3">{offering.subtitle}</p>
                      <p className="text-slate-600 leading-relaxed">{offering.description}</p>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  <div
                    className={`overflow-hidden transition-all duration-500 ${
                      isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    {/* Features */}
                    <div className="mt-8 mb-8">
                      <h4 className="text-lg font-semibold text-slate-900 mb-4">Key Features:</h4>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {offering.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <svg className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-slate-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Technologies */}
                    <div className="mb-8">
                      <h4 className="text-lg font-semibold text-slate-900 mb-4">Technologies Used:</h4>
                      <div className="flex flex-wrap gap-3">
                        {offering.technologies.map((tech, idx) => (
                          <span
                            key={idx}
                            className={`px-4 py-2 bg-gradient-to-r ${offering.color} text-white rounded-full text-sm font-semibold shadow-md`}
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Impact Metrics */}
                    <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 p-6 rounded-xl bg-gradient-to-br ${offering.bgColor}`}>
                      <div className="text-center">
                        <p className="text-sm font-semibold text-slate-600 mb-1">Revenue Potential</p>
                        <p className="text-xl font-bold text-slate-900">{offering.impactMetrics.revenue}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-semibold text-slate-600 mb-1">Efficiency Gain</p>
                        <p className="text-xl font-bold text-slate-900">{offering.impactMetrics.efficiency}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-semibold text-slate-600 mb-1">Market Reach</p>
                        <p className="text-xl font-bold text-slate-900">{offering.impactMetrics.reach}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom Gradient Bar */}
                <div className={`h-2 bg-gradient-to-r ${offering.color} ${isExpanded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`} />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default OfferingsSection;